import React from 'react';
import {
  StyleSheet,
  View,
  Text,
  TextInput,
  ScrollView,
} from './components/base_components';
import { BusyComponent, BusyOverlay } from './components/busy_component';

import UploaderProgressScreen from './uploader_progress_screen';
import UploaderSettingsOverlay from './components/overlays/uploader_settings_overlay';

import OrgSelect from './components/org_select';
import VolumeSelect from './components/volume_select';
import FileInput from './components/file_input';
import TextButton from './components/buttons/text_button';

import MediaIngest from './lib/media_ingest';
import { byteToString } from './tools/util';

const styles = StyleSheet.create({
  uploaderScreen: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'transparent',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
  },
  scroller: {
    flex: 1,
    alignSelf: 'stretch',
    flexDirection: 'column',
  },
  scrollInner: {
    paddingLeft: '3em',
    paddingRight: '3em',
    paddingTop: '1em',
    paddingBottom: '5em',
    alignSelf: 'stretch',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    overflowY: 'scroll',
  },
  title: {
    marginBottom: '0.4em',
    color: 'black',
    fontSize: '2.5em',
    fontWeight: 'bold',
  },
  step: {
    marginTop: '0.5em',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  stepTitle: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: '1.2em',
    color: 'black',
    fontSize: '1.5em',
    fontWeight: 'bold',
  },
  stepText: {
    color: 'black',
    fontSize: '1.5em',
    fontWeight: 'bold',
  },
  stepContents: {
    marginTop: '0.5em',
    height: '4em',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  input: {
    marginTop: '0.5em',
    width: '25em',
    height: '3em',
    paddingLeft: '1em',
    color: 'black',
    backgroundColor: '#ccc',
    borderRadius: '0.5em',
  },
  button: {
    width: '10em',
    height: '2em',
  },
  summary: {
    height: '3em',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
  },
  summaryText: {
    color: 'black',
    fontSize: '1em',
    fontWeight: 'bold',
  },
  settingsButton: {
    position: 'absolute',
    top: '1em',
    right: '1em',
    width: '10em',
    height: '2em',
  },
});

type Props = {};
type State = {
  upload_org: any;
  upload_volume: any;
  dest_dir: string;
  file_count: number;
  total_size: number;
  show_progress: boolean;
  show_settings: boolean;
};
export default class UploaderScreen extends BusyComponent<Props, State> {
  state = {
    upload_org: '1',
    upload_volume: '1',
    dest_dir: '/test9911',
    file_count: 0,
    total_size: 0,
    show_progress: false,
    show_settings: false,
  };
  _fileRef = React.createRef();
  componentDidMount() {
    MediaIngest.init();
  }
  componentWillUnmount() {}
  _onOrgChange = (upload_org) => this.setState({ upload_org });
  _onVolumeChange = (upload_volume) => this.setState({ upload_volume });
  _onDestChange = (dest_dir) => this.setState({ dest_dir });
  _onFileChange = async () => {
    const file_list = this._fileRef.current.getFiles();
    const { file_count, total_size } = MediaIngest.getSummary({ file_list });
    this.setState({ file_count, total_size });
  };
  _onUploadPress = () => {
    const { upload_org, upload_volume, dest_dir } = this.state;
    const file_list = this._fileRef.current.getFiles() || [];
    if (file_list.length > 0 && upload_volume && dest_dir && this.setBusy()) {
      this.setState({ show_progress: true });
      const opts = {
        org_id: parseInt(upload_org),
        file_list,
        volume_id: parseInt(upload_volume),
        dest_dir,
      };
      MediaIngest.ingest(opts, (err) => {
        console.log('ingest result:', err);
        this.clearBusy();
        if (err) {
          console.log('upload failed!');
        } else {
          console.log('upload success!');
        }
      });
    }
  };
  _onProgressHide = () => this.setState({ show_progress: false });
  _onSettingsShow = () => this.setState({ show_settings: true });
  _onSettingsHide = () => this.setState({ show_settings: false });
  render() {
    const {
      upload_org,
      upload_volume,
      dest_dir,
      file_count,
      total_size,
      show_progress,
      show_settings,
    } = this.state;
    let volume_select = null;
    if (upload_org && upload_org !== '0') {
      volume_select = (
        <VolumeSelect
          style={styles.select}
          onlyMedia={true}
          onlyOrgId={parseInt(upload_org)}
          value={upload_volume}
          onChange={this._onVolumeChange}
        />
      );
    } else {
      volume_select = <Text style={styles.detail}>Choose an org</Text>;
    }

    let summary = null;
    if (file_count > 0) {
      summary = (
        <Text style={styles.summaryText}>
          {`Total Files: ${file_count}, Total Size: ${byteToString(
            total_size
          )}`}
        </Text>
      );
    }
    let overlay = null;
    if (show_progress) {
      overlay = (
        <UploaderProgressScreen
          uploadOrg={upload_org}
          uploadVolume={upload_volume}
          destDir={dest_dir}
          onRetry={this._onUploadPress}
          onDone={this._onProgressHide}
        />
      );
    } else if (show_settings) {
      overlay = <UploaderSettingsOverlay onDone={this._onSettingsHide} />;
    }

    return (
      <View style={styles.uploaderScreen}>
        <ScrollView
          style={styles.scroller}
          contentContainerStyle={styles.scrollInner}
        >
          <Text style={styles.title}>ABT Uploader</Text>
          <Step number={0} text='Choose Org'>
            <OrgSelect
              style={styles.select}
              value={upload_org}
              onChange={this._onOrgChange}
            />
          </Step>
          <Step number={1} text='Choose Volume'>
            {volume_select}
          </Step>
          <Step number={2} text='Enter Destination Dir'>
            <TextInput
              style={styles.input}
              value={dest_dir}
              onChangeText={this._onDestChange}
            />
          </Step>
          <Step number={3} text='Choose Upload Folder'>
            <FileInput
              ref={this._fileRef}
              style={styles.fileInput}
              webkitdirectory={true}
              onChange={this._onFileChange}
            />
          </Step>
          <View style={styles.summary}>{summary}</View>
          <TextButton
            style={styles.button}
            text='Upload'
            onPress={this._onUploadPress}
          />
          <TextButton
            style={styles.settingsButton}
            text='Settings'
            onPress={this._onSettingsShow}
          />
        </ScrollView>
        <BusyOverlay isBusy={this.state.is_busy} />
        {overlay}
      </View>
    );
  }
}

function Step(props) {
  const { children, number, text } = props;
  return (
    <View style={styles.step}>
      <View style={styles.stepTitle}>
        <Text style={styles.stepNumber}>{number + '.'}</Text>
        <Text style={styles.stepText}>{text}</Text>
      </View>
      <View style={styles.stepContents}>{children}</View>
    </View>
  );
}
